import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Power } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CourseManagement = ({ onUpdate }) => {
  const [courses, setCourses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    curriculum: 'IGCSE',
    grade: '9',
    description: ''
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/courses`, { withCredentials: true });
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to fetch courses');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await axios.put(`${BACKEND_URL}/api/courses/${editingCourse.course_id}`, formData, { withCredentials: true });
        toast.success('Course updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/courses`, formData, { withCredentials: true });
        toast.success('Course created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCourses();
      onUpdate?.();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const toggleActive = async (courseId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/courses/${courseId}/toggle-active`, {}, { withCredentials: true });
      toast.success('Status updated');
      fetchCourses();
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/courses/${courseId}`, { withCredentials: true });
      toast.success('Course deleted');
      fetchCourses();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', curriculum: 'IGCSE', grade: '9', description: '' });
    setEditingCourse(null);
  };

  const openEditDialog = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      curriculum: course.curriculum,
      grade: course.grade,
      description: course.description || ''
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Manage Courses</h3>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-purple-700 hover:bg-purple-800">
              <Plus className="h-4 w-4 mr-2" />
              Add Course
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Course Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., IGCSE Chemistry Grade 10"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Curriculum</label>
                  <select
                    value={formData.curriculum}
                    onChange={(e) => setFormData({ ...formData, curriculum: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="IGCSE">IGCSE</option>
                    <option value="CBSE">CBSE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Grade</label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="9">Grade 9</option>
                    <option value="10">Grade 10</option>
                    <option value="11">Grade 11</option>
                    <option value="12">Grade 12</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Course description..."
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full bg-purple-700 hover:bg-purple-800">
                {editingCourse ? 'Update Course' : 'Create Course'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {courses.map((course) => (
          <div key={course.course_id} className="border border-purple-200 rounded-lg p-4 bg-white hover:shadow-md">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{course.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                <div className="flex gap-2 mt-2">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">{course.curriculum}</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Grade {course.grade}</span>
                  {course.is_active && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Active</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => toggleActive(course.course_id)} variant="ghost" size="sm">
                  <Power className={`h-4 w-4 ${course.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                </Button>
                <Button onClick={() => openEditDialog(course)} variant="ghost" size="sm">
                  <Edit className="h-4 w-4 text-blue-600" />
                </Button>
                <Button onClick={() => deleteCourse(course.course_id)} variant="ghost" size="sm">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {courses.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No courses yet. Create your first course!
        </div>
      )}
    </div>
  );
};

export default CourseManagement;
